'use client';

/**
 * Toast system — dependency-free, accessible notification primitives.
 *
 * Usage:
 *   const { toast } = useToast();
 *   toast.success('Task saved');   // role="status" aria-live="polite"
 *   toast.error('Could not save'); // role="alert"  aria-live="assertive"
 *   toast.info('Draft saved');     // role="status" aria-live="polite"
 *   toast.notification({ title, body, href }); // clickable, deep-links
 *
 * The `notification` variant renders a title + body and, when an `href` is
 * given, is a single clickable link that navigates and dismisses on click —
 * it's what surfaces a freshly-arrived in-app notification (see
 * NotificationRealtimeListener).
 *
 * ToastProvider must wrap the app (mounted in layout.tsx inside QueryProvider).
 * Container is fixed top-right z-[70] (above modals at z-50/60). The
 * `notification` variant is rendered in solid primary orange so a freshly
 * arrived notification stands out from the quieter success/info toasts.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import Link from 'next/link';
import { CheckCircle2, AlertCircle, Info, Bell, X } from 'lucide-react';
import clsx from 'clsx';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ToastType = 'success' | 'error' | 'info' | 'notification';

interface ToastItem {
  id: string;
  type: ToastType;
  /** Single-line message for the success/error/info variants. */
  message: string;
  /** Notification variant: bold heading line. */
  title?: string;
  /** Notification variant: secondary body line. */
  body?: string;
  /** Notification variant: when set, the toast is a clickable deep-link. */
  href?: string;
}

interface NotificationToastInput {
  title: string;
  body?: string;
  href?: string;
}

interface ToastContextValue {
  toast: {
    success: (message: string) => void;
    error: (message: string) => void;
    info: (message: string) => void;
    notification: (input: NotificationToastInput) => void;
  };
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ToastContext = createContext<ToastContextValue | null>(null);

// ---------------------------------------------------------------------------
// Timings (ms)
// ---------------------------------------------------------------------------

const DISMISS_DELAY: Record<ToastType, number> = {
  success: 4000,
  info: 4000,
  error: 6000,
  // Slightly longer — it's actionable (the user may want to click through).
  notification: 6000,
};

const MAX_VISIBLE = 3;

// ---------------------------------------------------------------------------
// Single toast item component
// ---------------------------------------------------------------------------

const borderByType: Record<ToastType, string> = {
  success: 'border-secondary/40',
  error: 'border-error/40',
  info: 'border-primary-light-border',
  notification: 'border-primary-light-border',
};

function ToastItemView({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: (id: string) => void;
}) {
  const isError = item.type === 'error';
  const isNotification = item.type === 'notification';

  const Icon =
    item.type === 'success'
      ? CheckCircle2
      : item.type === 'error'
        ? AlertCircle
        : isNotification
          ? Bell
          : Info;

  const iconColor = isNotification
    ? 'text-white'
    : item.type === 'success'
      ? 'text-secondary'
      : item.type === 'error'
        ? 'text-error'
        : 'text-primary';

  // The notification variant is a solid, high-contrast orange card; the others
  // stay white with a colored accent border.
  const focusRing = isNotification
    ? 'focus-visible:ring-white focus-visible:ring-offset-primary'
    : 'focus-visible:ring-primary focus-visible:ring-offset-surface';

  // Icon + text. Shared between the clickable (Link) and static layouts so the
  // notification variant renders identically whether or not it has an href.
  const inner = (
    <>
      <Icon
        className={clsx('mt-0.5 h-4 w-4 shrink-0', iconColor)}
        aria-hidden="true"
      />
      {isNotification ? (
        <span className="min-w-0 flex-1">
          <span className="block font-semibold leading-snug text-white">
            {item.title}
          </span>
          {item.body ? (
            <span className="mt-0.5 block text-xs leading-snug text-white/85 line-clamp-2">
              {item.body}
            </span>
          ) : null}
        </span>
      ) : (
        <span className="flex-1 leading-snug">{item.message}</span>
      )}
    </>
  );

  return (
    <div
      // a11y: success/info/notification → polite status; error → assertive alert
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={clsx(
        // Layout & shape
        'flex items-start gap-3 w-full max-w-sm',
        'rounded-[12px] border px-4 py-3 font-ui text-sm shadow-float',
        // Notification = solid prominent orange; others = white + accent border
        isNotification
          ? 'bg-primary border-primary text-white'
          : clsx('bg-surface text-ink', borderByType[item.type]),
        // Entrance animation (top-right container → slide from top) — respects
        // prefers-reduced-motion
        'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2',
        'motion-safe:duration-200',
      )}
    >
      {item.href ? (
        // Clickable notification — navigates (Next.js Link) and dismisses.
        <Link
          href={item.href}
          onClick={() => onDismiss(item.id)}
          className={clsx(
            'flex min-w-0 flex-1 items-start gap-3 rounded-[10px]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
            focusRing,
          )}
        >
          {inner}
        </Link>
      ) : (
        <div className="flex min-w-0 flex-1 items-start gap-3">{inner}</div>
      )}

      {/* Dismiss */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => onDismiss(item.id)}
        className={clsx(
          'ml-1 shrink-0 rounded-[10px] p-0.5',
          isNotification
            ? 'text-white/80 hover:bg-white/20 hover:text-white'
            : 'text-ink-muted hover:bg-surface-container hover:text-ink',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
          focusRing,
          'transition-colors',
        )}
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const add = useCallback(
    (
      type: ToastType,
      opts: { message?: string; title?: string; body?: string; href?: string },
    ) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setItems((prev) => {
        // Cap at MAX_VISIBLE — drop the oldest if over limit
        const next: ToastItem[] = [
          ...prev,
          {
            id,
            type,
            message: opts.message ?? '',
            title: opts.title,
            body: opts.body,
            href: opts.href,
          },
        ];
        return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
      });

      const delay = DISMISS_DELAY[type];
      setTimeout(() => {
        dismiss(id);
      }, delay);
    },
    [dismiss],
  );

  const toast = useMemo(
    () => ({
      success: (message: string) => add('success', { message }),
      error: (message: string) => add('error', { message }),
      info: (message: string) => add('info', { message }),
      notification: (input: NotificationToastInput) =>
        add('notification', {
          title: input.title,
          body: input.body,
          href: input.href,
        }),
    }),
    [add],
  );

  const contextValue = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* Fixed toast container — top-center, above modals (z-[70]) */}
      <div
        className="fixed top-4 left-1/2 -translate-x-1/2 z-[70] flex flex-col gap-2 items-center"
        aria-label="Notifications"
      >
        {items.map((item) => (
          <ToastItemView key={item.id} item={item} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Types (re-exported for consumers)
// ---------------------------------------------------------------------------

export type { ToastItem, ToastType, NotificationToastInput };
